import { SnackbarAlertWrapper } from 'components';
import { MemoizedErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import { debounce } from 'lodash';
import { ConfirmProvider } from 'material-ui-confirm';
import { SnackbarProvider } from 'notistack';
import { memo, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Routes } from 'shared/navigation';
import { store } from 'shared/redux/store';
import SwitchThemeProvider from 'shared/theme/SwitchThemeProvider';
import './index.css';

// Constants
const STALE_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
const DEBOUNCE_WAIT = 500; // Debounce wait time in milliseconds
const MAX_SNACKS = 3; // Maximum number of snackbars shown simultaneously

/**
 * Configure React Query client with default options
 * - Disable automatic refetching
 * - Set stale time to 5 minutes
 * - Disable retries
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: false,
      staleTime: STALE_TIME,
    },
  },
});

/**
 * Handle service worker messages with debouncing
 * Parses the payload and logs it to console
 */
const handleServiceWorkerMessage = debounce((event: MessageEvent) => {
  try {
    const payload = JSON.parse(event.data.data.payload);
    console.log('data from notif', payload);
  } catch (error) {
    console.error('Failed to parse service worker message:', error);
  }
}, DEBOUNCE_WAIT);

/**
 * Provider wrapper component
 * Memoized to prevent unnecessary re-renders of the provider tree
 */
const MemoizedProviders = memo(({ children }: { children: React.ReactNode }) => (
  <QueryClientProvider client={queryClient}>
    {/* Only show React Query Devtools in development */}
    {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}

    {/* Redux store provider */}
    <Provider store={store}>
      {/* Router for navigation */}
      <BrowserRouter>
        {/* Theme provider for dark/light mode */}
        <SwitchThemeProvider>
          {/* Snackbar notifications configuration */}
          <SnackbarProvider
            maxSnack={MAX_SNACKS}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {/* Dialog confirmation provider */}
            <ConfirmProvider>{children}</ConfirmProvider>
          </SnackbarProvider>
        </SwitchThemeProvider>
      </BrowserRouter>
    </Provider>
  </QueryClientProvider>
));

// Set display name for debugging
MemoizedProviders.displayName = 'MemoizedProviders';

/**
 * Main App component
 * Handles service worker setup and provides the app structure
 */
const App = () => {
  // Set up service worker message handling
  useEffect(() => {
    const serviceWorker = navigator.serviceWorker;

    // Check for service worker support
    if (!serviceWorker) {
      console.warn('Service Worker is not supported');
      return;
    }

    // Add message listener
    serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    // Cleanup function to remove listener
    return () => {
      serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  return (
    // Error boundary for catching React errors
    <MemoizedErrorBoundary>
      {/* Providers wrapper */}
      <MemoizedProviders>
        {/* Main routing */}
        <Routes />
        {/* Global snackbar alerts */}
        <SnackbarAlertWrapper />
      </MemoizedProviders>
    </MemoizedErrorBoundary>
  );
};

// Memoize the entire App component
export default memo(App);
